import * as React from 'react';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Dialog} from 'shells/dialog/dialog';
// @ts-expect-error - imported from .js/.jsx file
import {CancelIcon} from 'shells/icon/svg/cancel-icon';
// @ts-expect-error - imported from .js/.jsx file
import {RetiringIcon} from 'shells/icon/svg/retiring-icon';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';
import {OptionCard} from 'shells/option-card/option-card';
import {MarketingHeader} from 'shells/typography/marketing-header';
import {ModalBody} from 'shells/typography/modal-body';

import styles from './cancel-sequence-modal.module.css';

type Props = {
    recipientCount: number;
    onClose: () => void;
    onCancelSequence: () => Promise<any>;
    onRetireSequence: () => Promise<any>;
    onComplete: () => void;
    isCancelOnly?: boolean;
};

export function CancelSequenceModal(props: Props) {
    const [action, setAction] = React.useState<'canceling' | 'retiring'>(
        props.isCancelOnly ? 'canceling' : 'retiring'
    );
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [isConfirming, setIsConfirming] = React.useState<boolean>(false);

    const buttonRef = React.useRef(null);

    const recipientCountText =
        props.recipientCount === 1
            ? 'one current recipient'
            : `${props.recipientCount} current recipients`;

    const isRetiringSequence = action === 'retiring';

    const handleOnConfirmAction = () => {
        setIsConfirming(false);
        setIsSaving(true);

        const onConfirmAction = isRetiringSequence
            ? props.onRetireSequence
            : props.onCancelSequence;

        onConfirmAction().then(() => {
            setIsSaving(false);
        });
    };

    const cancelOption = (
        <OptionCard
            onClick={() => setAction('canceling')}
            title='Cancel everything'
            body='Stop sending remaining messages and do not complete next steps'
            icon={<CancelIcon />}
            isSelected={action === 'canceling'}
        />
    );

    const retireOption = (
        <OptionCard
            onClick={() => setAction('retiring')}
            title='Retire it'
            body='Send remaining messages and complete next steps before finishing'
            icon={<RetiringIcon />}
            isSelected={action === 'retiring'}
        />
    );

    return (
        // @ts-expect-error
        <Modal isOpen={true} maxHeight='600px' onClose={props.onClose}>
            <div className={styles['cancel-container']}>
                <MarketingHeader>How should this drip sequence end?</MarketingHeader>
                <ModalBody>This will impact {recipientCountText} and cannot be undone.</ModalBody>
                <div className={styles['options-container']}>
                    {props.isCancelOnly ? (
                        cancelOption
                    ) : (
                        <>
                            {retireOption}
                            {cancelOption}
                        </>
                    )}
                </div>
            </div>
            <FooterBar
                left={
                    <Button variant='text' size='big' onClick={props.onClose}>
                        Cancel
                    </Button>
                }
                right={
                    <SaveButton
                        getButtonRef={buttonRef}
                        buttonText={
                            isRetiringSequence
                                ? 'Retire this drip sequence'
                                : 'Cancel this drip sequence'
                        }
                        savingText={isRetiringSequence ? 'Retiring…' : 'Canceling…'}
                        successText={isRetiringSequence ? 'Retired' : 'Canceled'}
                        isSaving={isSaving}
                        variant='danger'
                        // @ts-expect-error
                        onClick={() => setIsConfirming(true)}
                        onClearSaveComplete={props.onComplete}
                        saveCompleteTimeout={2000}
                    />
                }
            />
            {isConfirming ? (
                <Dialog
                    anchor={buttonRef.current}
                    question={getConfirmationQuestion(recipientCountText, action)}
                    onPrimary={handleOnConfirmAction}
                    onSecondary={() => {
                        setIsConfirming(false);
                    }}
                    primaryText={isRetiringSequence ? 'Yes, retire' : 'Yes, cancel'}
                    secondaryText={isRetiringSequence ? 'No, don’t retire' : 'No, don’t cancel'}
                    location='top'
                    isWarning={true}
                />
            ) : undefined}
        </Modal>
    );
}

function getConfirmationQuestion(recipientText: string, action: 'retiring' | 'canceling'): string {
    return action === 'retiring'
        ? `This will complete the drip sequence for ${recipientText} before finishing and cannot be undone. Are you sure you’d like to retire?`
        : `This will stop the drip sequence for ${recipientText} and cannot be undone. Are you sure you’d like to cancel?`;
}
